
import { Component, Mixins } from 'vue-property-decorator';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import moment from 'moment';
import { debounce } from 'ts-debounce';
import HistoryTable, { FilteredPickupHistories } from '@/features/pickup-history/components/HistoryTable.vue';
import FiltersModal from '@/features/common/components/servicesFilter/FiltersModal.vue';
import CancelPickupModal from '@/features/pickup-history/components/CancelPickupModal.vue';
import DateRangePickerInput from '@/features/common/components/DateRangePickerInput.vue';
import { ScheduledPickupItem } from '@/features/schedule-pickup/types';
import { showErrorAlertDialog, showSuccessAlertDialog } from '@/features/common/helpers';
import { getPickupHistoryItems, cancelPickup } from '@/features/pickup-history/services/pickupHistory.services';
import { GlobalResponseType } from '@/features/common/types';

@Component({
  components: {
    HistoryTable,
    FiltersModal,
    CancelPickupModal,
    DateRangePickerInput,
  },
})
export default class History extends Mixins(ResponsivenessMixin) {
  private showFilters = false;

  private selectedCarriers: string[] = [];

  private searchField = '';

  private dateRangeInput = '';

  private dateCalendar: string[] = [];

  private isLoading = false;

  private isLoadMoreHint = true;

  private requestCallCount = 1;

  private pickupHistories: ScheduledPickupItem[] = [];

  private isFutureDayPickups = true;

  private isCanceledPickups = true;

  private order = true;

  private historyTableKey = 1;

  private dateSortingActive = false;

  private oopsShow = false;

  private cancelPickupResponse: GlobalResponseType | null = null;

  private cancelPickupModal: {
    show: boolean,
    pickup: ScheduledPickupItem | null,
  } = {
    show: false,
    pickup: null,
  };

  get filteredPickupHistories(): FilteredPickupHistories {
    const pickupFiltered: FilteredPickupHistories = {
      today: [],
      upcoming: [],
      history: [],
    };

    const formatter = 'YYYY-MM-DD';
    this.pickupHistories.forEach((pickup) => {
      const pickupDate = moment(pickup.pickupRequestDate, formatter);
      const nowDate = moment().local().format(formatter);

      if (pickupDate.isSame(nowDate, 'day')) { // today
        pickupFiltered.today.push(pickup);
      } else if (pickupDate.isAfter(nowDate)) { // upcoming
        pickupFiltered.upcoming.push(pickup);
      } else { // history
        pickupFiltered.history.push(pickup);
      }
    });

    return pickupFiltered;
  }

  async created(): Promise<void> {
    this.isLoading = true;

    const items = await getPickupHistoryItems();
    if (items === null) {
      showErrorAlertDialog();
    } else {
      this.pickupHistories = items;
    }

    this.isLoading = false;
  }

  private async fetchMorePickupHistories() {
    this.isLoading = true;

    const historyPickups = await getPickupHistoryItems({
      offset: 30 * this.requestCallCount,
      search: this.searchField,
      carriers: this.selectedCarriers,
      dateRange: this.dateCalendar,
      orderByDesc: this.order,
      showFutureDay: this.isFutureDayPickups,
      showCancelled: this.isCanceledPickups,
    });

    if (historyPickups && historyPickups.length) {
      this.pickupHistories = [...this.pickupHistories, ...historyPickups];
      this.requestCallCount += 1;
    }

    this.isLoadMoreHint = !!historyPickups && historyPickups.length > 0;
    this.isLoading = false;
  }

  private showFuturePickups() {
    this.debounceSearchHandler(this.searchField);
  }

  private showCanceledPickups() {
    this.debounceSearchHandler(this.searchField);
  }

  private searchHistoryPickups(value: string) {
    this.debounceSearch(value);
  }

  private debounceSearch = debounce(this.debounceSearchHandler, 500);

  private async debounceSearchHandler(value: string) {
    this.isLoading = true;

    this.requestCallCount = 1;
    const items = await getPickupHistoryItems({
      search: value,
      carriers: this.selectedCarriers,
      dateRange: this.dateCalendar,
      orderByDesc: this.order,
      showFutureDay: this.isFutureDayPickups,
      showCancelled: this.isCanceledPickups,
    });
    if (items === null) {
      showErrorAlertDialog();
    } else {
      this.isLoadMoreHint = !!items && items.length > 20;
      this.pickupHistories = items;
      this.historyTableKey += 1;
    }

    this.isLoading = false;
  }

  private async sortByCreatedDate() {
    this.order = !this.order;
    this.dateSortingActive = true;
    this.debounceSearchHandler(this.searchField);
  }

  private async applyDateRangeFilter() {
    this.debounceSearchHandler(this.searchField);
  }

  private async applyFilters(): Promise<void> {
    this.showFilters = false;
    this.debounceSearchHandler(this.searchField);
  }

  private cancelPickup(pickup: ScheduledPickupItem) {
    this.cancelPickupModal.pickup = pickup;
    this.cancelPickupModal.show = true;
  }

  private async confirmCancelPickup() {
    this.isLoading = true;

    this.cancelPickupResponse = await cancelPickup(
      this.cancelPickupModal.pickup?.id as number,
    );

    this.cancelPickupModal.show = false;

    if (this.cancelPickupResponse && this.cancelPickupResponse.successful) {
      this.debounceSearchHandler(this.searchField);
      showSuccessAlertDialog(this.$t('pickupHistory.cancelPickup.successfulAlert') as string);
      return;
    }

    this.oopsShow = true;
    this.isLoading = false;
  }
}
